import {groupBy} from 'lodash';
import {DateTime} from 'luxon';

import {
  ApiFlowContributingAreas,
  ForecastTimeSeries,
  Project,
  SeasonalSeriesType,
  SeriesType,
  Site,
  SiteType,
} from './api/project';
import {DashboardSection, FlowForecastColumns, RateVolumeMode, Source} from './api/series';

function checkSeriesType(site: Site, seriesType: SeriesType): boolean {
  return site.seriesTypes.includes(seriesType);
}

export function hasGaugeFlow(site: Site) {
  return checkSeriesType(site, SeriesType.GaugeFlow);
}

export function hasGaugeElevation(site: Site) {
  return checkSeriesType(site, SeriesType.GaugeElevation);
}

export function hasShortTermFlow(site: Site) {
  return checkSeriesType(site, SeriesType.FlowShortTerm);
}

export function hasShortTermWeather(site: Site) {
  return checkSeriesType(site, SeriesType.WeatherShortTerm);
}

export function hasRollingSeasonalFlow(site: Site) {
  return (
    checkSeriesType(site, SeriesType.FlowSeasonalRolling) ||
    checkSeriesType(site, SeriesType.FlowSeasonalRollingExtended)
  );
}

export function hasSeasonalFlow(site: Site) {
  return checkSeriesType(site, SeriesType.FlowSeasonalRolling);
}

export function hasAnnualFlow(site: Site) {
  return checkSeriesType(site, SeriesType.FlowSeasonalRollingExtended);
}

// Which dashboard section the seasonal components tag their queries with for the series they draw.
export function seasonalDashboardSection(seriesType: SeasonalSeriesType): DashboardSection {
  return seriesType === SeriesType.FlowSeasonalRolling
    ? DashboardSection.Seasonal
    : DashboardSection.Annual;
}

export function hasDailyLTAFlow(site: Site) {
  return Object.values(site.observationTimeSeries)
    .flat()
    .some((o) => o.source === Source.FLOW_HISTORICAL_DAILY);
}

export function hasFullReleaseSeasonalModel(site: Site): boolean {
  if (hasAnnualFlow(site)) {
    return site.forecastTimeSeries[SeriesType.FlowSeasonalRollingExtended].some(
      (series) => series.source === Source.HydroForecastSeasonal
    );
  } else if (hasSeasonalFlow(site)) {
    return site.forecastTimeSeries[SeriesType.FlowSeasonalRolling].some(
      (series) => series.source === Source.HydroForecastSeasonal
    );
  } else {
    return false;
  }
}

export function hasSurface(site: Site): boolean {
  return checkSeriesType(site, SeriesType.SurfaceObservations);
}

export function canUseBasinAveraging(
  site: Site | null,
  basinAveragingEnabled: boolean,
  flowContributingAreas: ApiFlowContributingAreas | null | undefined
): boolean {
  return (
    !!site &&
    basinAveragingEnabled &&
    site.siteType !== SiteType.SUBBASIN &&
    site.siteType !== SiteType.BASIN_COMBINED &&
    !!flowContributingAreas?.[site.id]
  );
}

const DATE_FORMAT = 'yyyy-MM-dd';
export function makeDateFromString(dateString: string, zone: string, endOfDay?: boolean): Date {
  let dt = DateTime.fromFormat(dateString, DATE_FORMAT, {zone});
  if (endOfDay) {
    dt = dt.endOf('day');
  }
  return dt.toJSDate();
}

export function makeStringFromDate(date: Date, zone: string): string {
  return DateTime.fromJSDate(date, {zone}).toFormat(DATE_FORMAT);
}

export function getHasHFShortTermMean(forecastTimeSeries: ForecastTimeSeries): boolean {
  return forecastTimeSeries[SeriesType.FlowShortTerm].some(
    (q) =>
      q.source === Source.HydroForecastShortTerm &&
      q.columns &&
      q.columns.indexOf(FlowForecastColumns.DISCHARGE_MEAN) > -1
  );
}

// Spans depend on whether the site has the extended (annual) series at all, not on the series the
// Seasonal component draws: a site with both series types gets the annual spans for either.
export function getSeasonalHorizon(site: Site): number {
  const forecastLengthDays = getSeasonalForecastLengthDays(site);
  if (forecastLengthDays) {
    return forecastLengthDays;
  }
  let horizon_days = 90;
  if (site.seriesTypes.includes(SeriesType.FlowSeasonalRollingExtended)) {
    horizon_days = 370;
  }
  return horizon_days;
}

export function getSeasonalHistoryDays(site: Site): number {
  const forecastLengthDays = getSeasonalForecastLengthDays(site);
  if (forecastLengthDays) {
    return forecastLengthDays;
  }
  let history_days = 91; // ~3 months
  if (site.seriesTypes.includes(SeriesType.FlowSeasonalRollingExtended)) {
    history_days = 181; // ~6 months
  }
  return history_days;
}

/** Site grouping utilities */
export const TOTAL_SITE_PATTERN = /-total$/;
export const LOCAL_SITE_PATTERN = /-(local|sideflow)$/;

export function createSiteGroupId(sId: string): string {
  return sId.replace(TOTAL_SITE_PATTERN, '').replace(LOCAL_SITE_PATTERN, '');
}

// Returns sites that are the only site in a group or the total site of a group
export const getGroupedSites = (project: Project) => {
  const groupedSites = groupBy(project.sites, (s) => createSiteGroupId(s.id));

  return project.sites?.filter((s) => {
    const group = groupedSites[createSiteGroupId(s.id)];
    return group.length === 1 || !LOCAL_SITE_PATTERN.test(s.id);
  });
};
export function getSeasonalForecastLengthDays(site: Site): number | undefined {
  const forecastLengthDays = site.forecastTimeSeries[SeriesType.FlowSeasonalRolling].find(
    (series) => series.source === Source.HydroForecastSeasonal
  )?.forecastLengthDays;
  return forecastLengthDays;
}
export function getDefaultRateVolumeMode(site: Site): RateVolumeMode {
  return site.defaultRateVolumeMode ?? 'rate';
}

export function makeValidRateVolumeMode(mode: RateVolumeMode, site: Site): RateVolumeMode {
  return isInvalidLevelMode(mode, site) ? 'rate' : mode;
}

export function isInvalidLevelMode(mode: RateVolumeMode, site: Site): boolean {
  return mode === 'level' && !site.seriesTypes.includes(SeriesType.Level);
}

export function isPublicCustomerProject(project: Project): boolean {
  /*
   * Determine whether a project is customer-visible and not
   * under construction.
   * Note that this doesn't capture organization-level ways
   * to disable projects, so active projects in inactive orgs will still
   * return true here.
   */
  if (project.name.includes('(Under Construction)')) {
    return false;
  }
  if (project.isHidden) {
    return false;
  }
  return true;
}
